import { UploadedFile } from '../models/uploaded-file.model';
import { PlasticsOrderedProduct } from '../models/plastics-ordered-product.model';

export type OrderReviewPriceSelection = NonNullable<PlasticsOrderedProduct['changeLog'][number]['orderReviewPriceSelection']>;

export function latestOrderReviewPriceSelection(line: PlasticsOrderedProduct): OrderReviewPriceSelection | null {
    const entry = (line.changeLog ?? []).filter(item => item.orderReviewPriceSelection)
        .sort((a, b) => +new Date(b.createdOn) - +new Date(a.createdOn))[0];
    return entry?.orderReviewPriceSelection ?? null;
}

export const ORDER_ACKNOWLEDGEMENT_FILE_PURPOSE = 'order-acknowledgement';

export function orderAcknowledgementFile(files: UploadedFile[] | null | undefined): UploadedFile | null {
    return [...(files ?? [])].reverse().find(file => file.active !== false
        && file.purpose === ORDER_ACKNOWLEDGEMENT_FILE_PURPOSE) ?? null;
}

export interface OrderFinalizeLineInput {
    partNumber: string;
    status: string;
    pricingChecked?: boolean;
    estimatingConfirmed?: boolean;
    note?: string;
    consistencyFinding?: string | null;
    consistencyConfirmed?: boolean;
    pricingFinding?: string | null;
    estimatingFinding?: string | null;
    estimatingMode?: string | null;
    followUps?: { status: string; savedNote: string }[];
}

export function lineFinalizeBlockers(line: OrderFinalizeLineInput, reopenedRelease = false): string[] {
    const blockers: string[] = [];
    if (line.status !== 'Draft' && !(reopenedRelease && ['Released', 'Scheduled'].includes(line.status))) blockers.push('not in Draft');
    if (line.consistencyFinding !== 'clear' && !line.consistencyConfirmed) {
        blockers.push(line.consistencyFinding === 'issue' ? 'PO inconsistency'
            : line.consistencyFinding === 'unverified' ? 'PO source needed' : 'PO not analyzed');
    }
    if (!(line.pricingFinding === 'not-applicable' || !!line.pricingChecked)) {
        blockers.push(!line.pricingFinding ? 'pricing not analyzed'
            : line.pricingFinding === 'match' ? 'pricing not confirmed' : 'pricing not ready');
    }
    if (!(line.estimatingMode === 'notApplicable' || !!line.estimatingConfirmed)) {
        blockers.push(!line.estimatingFinding ? 'schedule not analyzed'
            : line.estimatingFinding === 'uncertain' ? 'production estimate needs review' : 'scheduling not confirmed');
    }
    if ((line.followUps ?? []).some(check => check.status !== 'complete' || check.savedNote !== (line.note || ''))) {
        blockers.push('follow-ups incomplete');
    }
    return blockers;
}

export function orderFinalizeReadiness(args: {
    finalized: boolean;
    reopenedRelease?: boolean;
    lines: OrderFinalizeLineInput[];
}): { canFinalize: boolean; reasons: string[] } {
    if (!args.lines.length) return { canFinalize: false, reasons: ['the order has no lines'] };
    // Repeat Finalize replaces the stored PDF. Released lines and saved costs remain unchanged.
    if (args.finalized) return { canFinalize: true, reasons: [] };
    const reasons = args.lines.flatMap(line => lineFinalizeBlockers(line, args.reopenedRelease)
        .map(reason => `${line.partNumber || 'line'}: ${reason}`));
    return { canFinalize: reasons.length === 0, reasons };
}
